import { useEffect } from 'react';

import BarChart from '@App/components/BarChart/BarChart';

import { useTranslation } from '@utils/i18n/i18n.consumers';
import { RISK_COLORS } from '@App/pages/RiskDataPage/risk-data-page.constants';
import { RISK_DATA_KASUNGU } from '@App/pages/RiskDataPage/risk-data';
import { GLOSSARY_ITEMS } from '@App/components/Navbar/GlossarySearch/glossary-search.constants';

import DefinitionComponent from '@App/components/DefinitionComponent/DefinitionComponent';
import RiskDataStories from '../RiskDataStories/RiskDataStories';
import { getMaxValue } from '../risk-data-content.utils';

import './risk-data-extreme-precipitation.styl';

function RiskDataExtremePrecipitation({
  selectedScenario,
  selectedHorizon,
  selectedRisk,
  map,
}) {
  const { t } = useTranslation();

  useEffect(() => {
    if (map && selectedRisk === 'extreme-precipitation') {
      map.setLayoutProperty('malawi-regions-fill', 'visibility', 'none');

      if (!map.getSource('flood-risk-basins')) {
        map.addSource('flood-risk-basins', {
          type: 'geojson',
          data: '/flood-risk-basins.geojson',
          promoteId: 'name_1',
        });
      }

      if (!map.getLayer('flood-risk-basins-fill')) {
        map.addLayer({
          source: 'flood-risk-basins',
          id: 'flood-risk-basins-fill',
          type: 'fill',
          paint: {
            'fill-color': [
              'match',
              ['get', 'RISK'],
              ...Object.keys(RISK_COLORS).flatMap(risk => [risk, RISK_COLORS[risk]]),
              '#fff',
            ],
            'fill-opacity': 0.5,
            'fill-outline-color': 'transparent',
          },
        }, 'River labels');
      }
    }

    return () => {
      if (map) {
        if (map.getLayer('flood-risk-basins-fill')) {
          map.removeLayer('flood-risk-basins-fill');
        }

        if (map.getSource('flood-risk-basins')) {
          map.removeSource('flood-risk-basins');
        }

        map.setLayoutProperty('malawi-regions-fill', 'visibility', 'visible');
      }
    };
  }, [map, selectedRisk]);

  return (
    <div className="risk-data-extreme-precipitation">
      <h3>{t('Introduction')}</h3>
      <p>
        {t('Excess of rainfall can cause flooding, a frequent natural hazard that occurs when an overflow of water submerges land that is usually dry. Fluvial (or riverine) ')}
        <DefinitionComponent
          id={31}
          term="Floods"
          definition={GLOSSARY_ITEMS.find(item => item.id === 31)?.description}
          learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 31)?.url}
          lowercase
        />
        {t(' occur when intense rainfall over an extended period of time causes a river to exceed its capacity, triggering the overflow or breaching of the river embankments, and ultimately causing the submersion of land. Pluvial (or surface) flooding is also a consequence of heavy rainfall, but they happen outside of a river, usually in an urban area. Pluvial floods can happen due to rainfall run-off from hillside areas when degraded soil or lack of natural vegetation reduces the absorbing capacity of the soil. Hillsides hit by wildfires for instance can be sources of pluvial floods when hit by strong rainfall.')}
       
      </p>
      <p>
        {t('Floods can cause widespread devastation, resulting in loss of life and damage to personal property and critical public health infrastructure. ')}
        <DefinitionComponent
          id={10}
          term="Climate change"
          definition={GLOSSARY_ITEMS.find(item => item.id === 10)?.description}
          learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 10)?.url}
        />
        {t(' is increasing the frequency and intensity of floods, and their impacts are exacerbated by land use changes and increased population pressure on flood-prone areas.')}
      </p>
      <p>
        {t('Note that this risk is evaluated by considering the percentage of population expected to be affected by riverine flooding in an average year and considering existing levels of flood protection in the entire river basin. This indicator represents flood risk not in terms of maximum possible impact but rather as average annual impact. The impacts from infrequent, extreme flood years are averaged with more common, less newsworthy flood years to produce the “expected annual affected population.” Higher values indicate that a greater proportion of the population is expected to be impacted by riverine floods on average (shown on the map).')}
      </p>
      <p>
        {t('There is no specific information for projected changes in flooding in the Kasungu region. To have an indication about how the risk of flooding is projected to change in the future, one can look for instance at the accumulated rainfall that falls, on average, on the top 5% rainiest days of the year. If this number increases significantly, one can expect rainfall to become more intense, increasing the risk of flooding.')}
      </p>
      <p>
        {t('The accumulated rainfall on the 5% rainiest days was 132.8mm in the recent past (1995-2024) in Kasungu, with a large year to year variability of 70.4mm. This is projected to increase by less than 6.55mm for the low emissions scenario, and less than 8.5mm for the highest one. These increases fall well within the range of historical year to year variability, suggesting that increases in the more extreme rainfall are not large enough to significantly change the current risk of flooding in the region.')}
      </p>
      <BarChart
        data={RISK_DATA_KASUNGU['extreme-precipitation'][selectedScenario.id]}
        selectedItem={selectedHorizon.id}
        title={t('Extreme rainfall in the scenario of {{selectedScenario}}', { selectedScenario: t(selectedScenario.label).toLowerCase() })}
        xDataKey="horizon"
        yAxisLabel={t('mm')}
        yDataKey="value"
        yDomain={[0, getMaxValue(RISK_DATA_KASUNGU['extreme-precipitation'])]}
      />
      <p>
        {t('For the South of Malawi, where extreme rainfall could be associated with tropical storms, projected changes in accumulated rainfall on the rainiest 5% of the days are also small when compared to the year-to-year variability over the recent past (1995-2024). This again suggests that the current risk of flooding in the region, which is high at present, is not projected to change significantly.')}
      </p>
      <div className="how-does-this-affect-me__wrapper">
        <h3>
          {t('How does this affect me?')}
        </h3>
        <p>
          {t('River floods that occur very quickly are the most likely to cause immediate injury. Floods can also have other more indirect impacts such as water- and vector-borne diseases, injuries, food security and nutrition, mental health effects, disrupted health systems, damages to infrastructure etc…')}
        </p>
        <p>
          {t('People with limited mobility are affected more as well as people with limited access to health care and clean drinking water and farming communities.')}
        </p>
      </div>
      <h3>
        {t('How can we adapt?')}
      </h3>
      <p>
        {t('The flood risk can be reduced in a number of ways:')}
        <ul>
          <li>
            <DefinitionComponent
              id={26}
              term="Early warning systems"
              definition={GLOSSARY_ITEMS.find(item => item.id === 26)?.description}
              learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 26)?.url}
            />
            {t(' to understand when a flood might happen')}
          </li>
          <li>{t('Mapping of flood prone zones to understand where floods are most likely to happen')}</li>
          <li>{t('Increasing water retention capacity and slowing of runoff')}</li>
          <li>{t('River and basin management')}</li>
          <li>{t('Evacuation procedures')}</li>
          <li>{t('Public awareness and education')}</li>
        </ul>
      </p>
      <RiskDataStories hazardCategories={['extreme-precipitation', 'floods']} />
    </div>
  );
}

export default RiskDataExtremePrecipitation;